import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STATUS_META, STATUS_ORDER, PRIORITY_META, TYPE_META, initials, fmtDate, timeAgo } from "@/lib/brand";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Send, Trash2, X, UserPlus, History, GitCommitHorizontal, Users } from "lucide-react";
import Attachments from "@/components/Attachments";
import { toast } from "sonner";

export default function TicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isStaff = user.role !== "client";
  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [activity, setActivity] = useState([]);
  const [people, setPeople] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [mentionIds, setMentionIds] = useState([]);
  const [mentionAll, setMentionAll] = useState(false);
  const [suggest, setSuggest] = useState({ open: false, items: [], start: 0, pos: 0 });

  const load = async () => {
    const [t, c, a] = await Promise.all([
      api.get(`/tickets/${id}`),
      api.get(`/tickets/${id}/comments`),
      api.get(`/tickets/${id}/activity`),
    ]);
    setTicket(t.data);
    setComments(c.data);
    setActivity(a.data);
    api.get(`/assignable-users?client_id=${t.data.client_id}`).then((r) => setPeople(r.data));
  };
  useEffect(() => { load(); }, [id]);

  const refreshActivity = async () => {
    const a = await api.get(`/tickets/${id}/activity`);
    setActivity(a.data);
  };

  const patch = async (field, value) => {
    const { data } = await api.patch(`/tickets/${id}`, { [field]: value });
    setTicket(data);
    refreshActivity();
    toast.success("Updated");
  };

  const addWatcher = async (uid) => {
    const cur = ticket.watchers || [];
    if (cur.includes(uid)) return;
    await patch("watchers", [...cur, uid]);
  };
  const removeWatcher = async (uid) => {
    await patch("watchers", (ticket.watchers || []).filter((w) => w !== uid));
  };

  const addComment = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      const mentions = mentionIds.filter((mid) => {
        const p = people.find((x) => x.id === mid);
        return p && body.includes(`@${p.name}`);
      });
      const mention_all = mentionAll && (/@here\b/i.test(body) || /@team\b/i.test(body));
      await api.post(`/tickets/${id}/comments`, { body, mentions, mention_all });
      setBody("");
      setMentionIds([]);
      setMentionAll(false);
      setSuggest({ open: false, items: [], start: 0, pos: 0 });
      const [c, a] = await Promise.all([api.get(`/tickets/${id}/comments`), api.get(`/tickets/${id}/activity`)]);
      setComments(c.data);
      setActivity(a.data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const onBodyChange = (e) => {
    const val = e.target.value;
    setBody(val);
    const pos = e.target.selectionStart;
    const before = val.slice(0, pos);
    const m = before.match(/@([^\s@]{0,30})$/);
    if (m) {
      const q = m[1].toLowerCase();
      const specials = [
        { id: "__here__", name: "here", role: "everyone on this ticket", special: true, token: "here" },
        { id: "__team__", name: "team", role: "everyone on this ticket", special: true, token: "team" },
      ].filter((s) => s.token.startsWith(q) || q === "");
      const items = [...specials, ...people.filter((p) => p.name.toLowerCase().includes(q))].slice(0, 7);
      setSuggest({ open: items.length > 0, items, start: pos - m[0].length, pos });
    } else {
      setSuggest((s) => ({ ...s, open: false }));
    }
  };

  const pickMention = (p) => {
    const token = p.special ? p.token : p.name;
    const nb = body.slice(0, suggest.start) + `@${token} ` + body.slice(suggest.pos);
    setBody(nb);
    if (p.special) setMentionAll(true);
    else setMentionIds((ids) => (ids.includes(p.id) ? ids : [...ids, p.id]));
    setSuggest({ open: false, items: [], start: 0, pos: 0 });
  };

  const remove = async () => {
    await api.delete(`/tickets/${id}`);
    toast.success("Ticket deleted");
    navigate("/tickets");
  };

  if (!ticket) return <div className="h-40 rounded-md bg-white border border-slate-200 animate-pulse" />;

  const watcherIds = ticket.watchers || [];
  const available = people.filter((p) => !watcherIds.includes(p.id) && p.id !== ticket.assignee_id);

  return (
    <div className="animate-fade-up max-w-5xl mx-auto">
      <button onClick={() => navigate("/tickets")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4" data-testid="back-btn">
        <ArrowLeft size={15} /> Back to tickets
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6 border-slate-200">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-slate-100 text-slate-600">{TYPE_META[ticket.type]}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded ${PRIORITY_META[ticket.priority].bg}`}>{PRIORITY_META[ticket.priority].label}</span>
              <span className="flex items-center gap-1.5 text-xs text-slate-500">
                <span className="h-2 w-2 rounded-full" style={{ background: STATUS_META[ticket.status].dot }} />
                {STATUS_META[ticket.status].label}
              </span>
              {ticket.project_name && (
                <button onClick={() => navigate(`/projects/${ticket.project_id}`)} className="text-[10px] px-2 py-0.5 rounded bg-sky-50 text-sky-700 hover:bg-sky-100" data-testid="ticket-project-chip">
                  📁 {ticket.project_name}
                </button>
              )}
            </div>
            <h1 className="font-display text-2xl font-bold text-slate-900 tracking-tight" data-testid="ticket-title">
              {ticket.number ? <span className="text-slate-400 font-semibold mr-2">#{ticket.number}</span> : null}
              {ticket.title}
            </h1>
            <p className="text-xs text-slate-400 mt-2">
              {ticket.client_name} · opened by {ticket.created_by_name} · {fmtDate(ticket.created_at)}
            </p>
            <p className="text-sm text-slate-700 leading-relaxed mt-4 whitespace-pre-wrap">
              {ticket.description || "No description provided."}
            </p>
          </Card>

          <Card className="p-6 border-slate-200">
            <p className="font-display font-semibold text-slate-900 mb-4">Discussion ({comments.length})</p>
            <div className="space-y-4 mb-5">
              {comments.length === 0 && <p className="text-sm text-slate-400">No comments yet. Start the conversation.</p>}
              {comments.map((c) => (
                <div key={c.id} className="flex gap-3" data-testid="comment-item">
                  <Avatar className="h-8 w-8 shrink-0">
                    <AvatarFallback className="text-white text-[11px]" style={{ background: c.author_role === "client" ? "#1E3A8A" : "var(--cp-primary)" }}>
                      {initials(c.author_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-800">{c.author_name}</span>
                      <span className="text-[10px] uppercase tracking-wider text-slate-400">{c.author_role}</span>
                      <span className="text-[11px] text-slate-400">· {timeAgo(c.created_at)}</span>
                    </div>
                    <p className="text-sm text-slate-700 mt-0.5 whitespace-pre-wrap">{renderMentions(c.body, people)}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="relative border-t border-slate-100 pt-4">
              <div className="flex gap-2 items-end">
                <Textarea value={body} onChange={onBodyChange} rows={2} placeholder="Write a reply… use @ to mention someone" data-testid="comment-input" className="resize-none" />
                <Button onClick={addComment} disabled={sending} className="text-white shrink-0" style={{ background: "var(--tenant-primary)" }} data-testid="send-comment-btn">
                  <Send size={16} />
                </Button>
              </div>
              {suggest.open && (
                <div className="absolute bottom-full mb-1 left-0 w-64 bg-white border border-slate-200 rounded-md shadow-lg z-20 overflow-hidden" data-testid="mention-suggestions">
                  {suggest.items.map((p) => (
                    <button key={p.id} onClick={() => pickMention(p)} data-testid="mention-option"
                      className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 text-left">
                      {p.special ? (
                        <span className="h-6 w-6 rounded-full flex items-center justify-center" style={{ background: "var(--tenant-primary)" }}><Users size={13} className="text-white" /></span>
                      ) : (
                        <Avatar className="h-6 w-6"><AvatarFallback className="text-white text-[10px]" style={{ background: p.role === "client" ? "#1E3A8A" : "var(--cp-primary)" }}>{initials(p.name)}</AvatarFallback></Avatar>
                      )}
                      <span className="text-sm text-slate-800 flex-1 truncate">@{p.name}</span>
                      <span className="text-[10px] uppercase tracking-wider text-slate-400">{p.role}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </Card>

          <Attachments ticketId={id} />

          <Card className="p-6 border-slate-200" data-testid="activity-card">
            <p className="font-display font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <History size={16} style={{ color: "var(--tenant-primary)" }} /> Change history ({activity.length})
            </p>
            <div className="relative pl-5">
              <div className="absolute left-[7px] top-1 bottom-1 w-px bg-slate-200" />
              <div className="space-y-4">
                {activity.length === 0 && <p className="text-sm text-slate-400">No activity yet.</p>}
                {activity.map((a) => (
                  <div key={a.id} className="relative" data-testid="activity-item">
                    <span className="absolute -left-[18px] top-1 h-3 w-3 rounded-full border-2 border-white" style={{ background: "var(--tenant-primary)" }} />
                    <p className="text-sm text-slate-700">
                      <span className="font-medium text-slate-800">{a.actor_name}</span> {a.detail}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-0.5">{a.actor_role} · {timeAgo(a.created_at)}</p>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </div>

        <div className="space-y-4">
          <Card className="p-5 border-slate-200 space-y-4">
            <p className="font-display font-semibold text-slate-900">Details</p>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Status</Label>
              <Select value={ticket.status} onValueChange={(v) => patch("status", v)}>
                <SelectTrigger className="mt-1.5" data-testid="detail-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Priority</Label>
              <Select value={ticket.priority} onValueChange={(v) => patch("priority", v)}>
                <SelectTrigger className="mt-1.5" data-testid="detail-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(PRIORITY_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Assignee</Label>
              <Select value={ticket.assignee_id || "none"} onValueChange={(v) => patch("assignee_id", v === "none" ? "" : v)}>
                <SelectTrigger className="mt-1.5" data-testid="detail-assignee-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {people.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><UserPlus size={13} /> Resources / Watchers</Label>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {(ticket.watcher_details || []).map((w) => (
                  <span key={w.id} data-testid="watcher-chip" className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-700 rounded-full pl-2 pr-1 py-0.5">
                    {w.name}
                    <button onClick={() => removeWatcher(w.id)} className="hover:bg-slate-300 rounded-full p-0.5" data-testid="remove-watcher-btn"><X size={11} /></button>
                  </span>
                ))}
                {watcherIds.length === 0 && <span className="text-xs text-slate-400">None added</span>}
              </div>
              {available.length > 0 && (
                <Select value="" onValueChange={addWatcher}>
                  <SelectTrigger className="mt-2 h-8 text-xs" data-testid="add-watcher-select"><SelectValue placeholder="+ Add resource" /></SelectTrigger>
                  <SelectContent>
                    {available.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="text-xs text-slate-400 pt-1 flex items-center gap-1">
              <GitCommitHorizontal size={13} /> Last updated {timeAgo(ticket.updated_at)}
            </div>
          </Card>

          {isStaff && (
            <Button variant="outline" onClick={remove} className="w-full text-red-600 border-red-200 hover:bg-red-50" data-testid="delete-ticket-btn">
              <Trash2 size={15} className="mr-1.5" /> Delete ticket
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function renderMentions(text, people) {
  if (!text) return text;
  const names = people.map((p) => p.name).filter(Boolean).concat(["here", "team"]).sort((a, b) => b.length - a.length);
  if (names.length === 0) return text;
  const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const pattern = new RegExp("@(" + names.map(esc).join("|") + ")\\b", "g");
  const parts = [];
  let last = 0;
  let m;
  while ((m = pattern.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(
      <span key={m.index} className="text-sky-600 font-medium bg-sky-50 rounded px-1">{m[0]}</span>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}
